import React, { useEffect, useState } from 'react';
import { profileApi, goalsApi, projectsApi, infoApi, contactsApi, vehiclesApi, habitsApi, bookmarksApi, blogsApi } from '../api/client';
import type { Profile, Goals, Projects, Contact, Vehicle, HabitDefinition, Bookmark, Blog, BlogPost } from '../api/client';
import AddGoalModal from '../components/AddGoalModal';
import AddBookmarkModal from '../components/AddBookmarkModal';
import AddContactModal from '../components/AddContactModal';
import AddProjectModal from '../components/AddProjectModal';
import AddHabitModal from '../components/AddHabitModal';
import LogHabitModal from '../components/LogHabitModal';
import AddVehicleModal from '../components/AddVehicleModal';
import AddBlogModal from '../components/AddBlogModal';
import './Dashboard.css';

const Dashboard: React.FC = () => {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [goals, setGoals] = useState<Goals | null>(null);
  const [projects, setProjects] = useState<Projects | null>(null);
  const [apiInfo, setApiInfo] = useState<{ name: string; version: string; status: string } | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [habits, setHabits] = useState<HabitDefinition[]>([]);
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [showAddGoalModal, setShowAddGoalModal] = useState(false);
  const [showAddBookmarkModal, setShowAddBookmarkModal] = useState(false);
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  const [showAddHabitModal, setShowAddHabitModal] = useState(false);
  const [showLogHabitModal, setShowLogHabitModal] = useState(false);
  const [showAddVehicleModal, setShowAddVehicleModal] = useState(false);
  const [showAddBlogModal, setShowAddBlogModal] = useState(false);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [profileRes, goalsRes, projectsRes, apiInfoRes, contactsRes, vehiclesRes, habitsRes, bookmarksRes, blogsRes, blogPostsRes] = await Promise.all([
        profileApi.getProfile().catch(() => ({ data: null })),
        goalsApi.getGoals().catch(() => ({ data: null })),
        projectsApi.getProjects().catch(() => ({ data: null })),
        infoApi.getApiInfo().catch(() => ({ data: null })),
        contactsApi.getContacts().catch(() => ({ data: [] })),
        vehiclesApi.getVehicles().catch(() => ({ data: [] })),
        habitsApi.getHabitDefinitions().catch(() => ({ data: [] })),
        bookmarksApi.getBookmarks().catch(() => ({ data: [] })),
        blogsApi.getBlogs().catch(() => ({ data: [] })),
        blogsApi.getBlogPosts().catch(() => ({ data: [] })),
      ]);

      setProfile(profileRes.data);
      setGoals(goalsRes.data);
      setProjects(projectsRes.data);
      setApiInfo(apiInfoRes.data);
      setContacts(contactsRes.data || []);
      setVehicles(vehiclesRes.data || []);
      setHabits(habitsRes.data || []);
      setBookmarks(bookmarksRes.data || []);
      setBlogs(blogsRes.data || []);
      setBlogPosts(blogPostsRes.data || []);
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Refresh handlers for modals
  const handleGoalAdded = () => {
    fetchDashboardData();
  };

  const handleBookmarkAdded = () => {
    fetchDashboardData();
  };

  const handleContactAdded = () => {
    fetchDashboardData();
  };

  const handleProjectAdded = () => {
    fetchDashboardData();
  };

  const handleHabitAdded = () => {
    fetchDashboardData();
  };

  const handleHabitLogged = () => {
    fetchDashboardData();
  };

  const handleVehicleAdded = () => {
    fetchDashboardData();
  };

  const handleBlogAdded = () => {
    fetchDashboardData();
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading">Loading dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <h1>Dashboard</h1>
        <p>Welcome to SelfServe Dashboard</p>
      </header>

      <div className="dashboard-grid">
        {/* API Status Card */}
        <div className="dashboard-card">
          <h2>🔧 API Status</h2>
          {apiInfo ? (
            <div className="card-content">
              <p><strong>Name:</strong> {apiInfo.name}</p>
              <p><strong>Version:</strong> {apiInfo.version}</p>
              <p><strong>Status:</strong> <span className="status-badge">{apiInfo.status}</span></p>
            </div>
          ) : (
            <p className="no-data">API information not available</p>
          )}
        </div>

        {/* Profile Summary Card */}
        <div className="dashboard-card">
          <h2>👤 Profile Summary</h2>
          {profile ? (
            <div className="card-content">
              <p><strong>Name:</strong> {profile.name}</p>
              <p><strong>Profession:</strong> {profile.profession}</p>
              <p><strong>Location:</strong> {profile.location}</p>
              <p><strong>Interests:</strong> {profile.interests?.slice(0, 3).join(', ')}
                {profile.interests?.length > 3 && '...'}</p>
            </div>
          ) : (
            <p className="no-data">Profile not available</p>
          )}
        </div>

        {/* Goals Summary Card */}
        <div className="dashboard-card">
          <h2>🎯 Goals Overview</h2>
          {goals ? (
            <div className="card-content">
              <div className="goals-section">
                <h4>Short-term Goals ({goals.shortTerm?.length || 0})</h4>
                <ul>
                  {goals.shortTerm?.slice(0, 3).map((goal, index) => (
                    <li key={goal.id || index}>
                      <strong>{goal.goal}</strong>
                      <span className={`goal-priority priority-${goal.priority}`}> - {goal.priority} priority</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="goals-section">
                <h4>Long-term Goals ({goals.longTerm?.length || 0})</h4>
                <ul>
                  {goals.longTerm?.slice(0, 3).map((goal, index) => (
                    <li key={goal.id || index}>
                      <strong>{goal.goal}</strong>
                      <span className={`goal-priority priority-${goal.priority}`}> - {goal.priority} priority</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : (
            <p className="no-data">Goals not available</p>
          )}
        </div>

        {/* Projects Summary Card */}
        <div className="dashboard-card">
          <h2>💼 Projects Overview</h2>
          {projects ? (
            <div className="card-content">
              <div className="projects-section">
                <h4>Active Projects ({projects.activeProjects?.length || 0})</h4>
                <ul>
                  {projects.activeProjects?.slice(0, 3).map((project, index) => (
                    <li key={index}>
                      <strong>{project.name}</strong>
                      {project.status && <span className="project-status"> - {project.status}</span>}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="projects-section">
                <h4>Past Projects ({projects.pastProjects?.length || 0})</h4>
                <ul>
                  {projects.pastProjects?.slice(0, 2).map((project, index) => (
                    <li key={index}>{project.name}</li>
                  ))}
                </ul>
              </div>
            </div>
          ) : (
            <p className="no-data">Projects not available</p>
          )}
        </div>

        {/* Quick Actions Card */}
        <div className="dashboard-card">
          <h2>⚡ Quick Actions</h2>
          <div className="card-content">
            <div className="quick-actions">
              <button className="action-btn" onClick={() => setShowAddGoalModal(true)}>Add New Goal</button>
              <button className="action-btn" onClick={() => setShowAddProjectModal(true)}>Create Project</button>
              <button className="action-btn" onClick={() => setShowAddHabitModal(true)}>Create Habit</button>
              <button className="action-btn" onClick={() => setShowLogHabitModal(true)}>Log Habit</button>
              <button className="action-btn" onClick={() => setShowAddContactModal(true)}>Add Contact</button>
              <button className="action-btn" onClick={() => setShowAddBookmarkModal(true)}>Add Bookmark</button>
              <button className="action-btn" onClick={() => setShowAddBlogModal(true)}>Create Blog</button>
              <button className="action-btn" onClick={() => setShowAddVehicleModal(true)}>Add Vehicle</button>
            </div>
          </div>
        </div>

        {/* Social & Personal Data Card */}
        <div className="dashboard-card">
          <h2>🌐 Social & Personal</h2>
          <div className="card-content">
            <div className="data-stats">
              <div className="stat-item">
                <span className="stat-number">{contacts.length}</span>
                <span className="stat-label">Contacts</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{bookmarks.length}</span>
                <span className="stat-label">Bookmarks</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{bookmarks.filter(b => b.isFavorite).length}</span>
                <span className="stat-label">Favorites</span>
              </div>
            </div>
            {contacts.length > 0 && (
              <div className="detail-section">
                <p><strong>Top Contact Categories:</strong></p>
                <div className="category-summary">
                  {['professional', 'friend', 'family'].map(category => {
                    const count = contacts.filter(c => c.category === category).length;
                    return count > 0 ? (
                      <span key={category} className="category-tag">
                        {category}: {count}
                      </span>
                    ) : null;
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Lifestyle & Assets Card */}
        <div className="dashboard-card">
          <h2>🚗 Assets & Habits</h2>
          <div className="card-content">
            <div className="data-stats">
              <div className="stat-item">
                <span className="stat-number">{vehicles.length}</span>
                <span className="stat-label">Vehicles</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{habits.length}</span>
                <span className="stat-label">Habits Tracked</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{habits.filter(h => h.isActive).length}</span>
                <span className="stat-label">Active Habits</span>
              </div>
            </div>
            {habits.length > 0 && (
              <div className="detail-section">
                <p><strong>Habit Categories:</strong></p>
                <div className="category-summary">
                  {['health', 'productivity', 'personal'].map(category => {
                    const count = habits.filter(h => h.category === category).length;
                    return count > 0 ? (
                      <span key={category} className="category-tag">
                        {category}: {count}
                      </span>
                    ) : null;
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Content & Writing Card */}
        <div className="dashboard-card">
          <h2>📝 Content & Writing</h2>
          <div className="card-content">
            <div className="data-stats">
              <div className="stat-item">
                <span className="stat-number">{blogs.length}</span>
                <span className="stat-label">Blogs</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{blogPosts.length}</span>
                <span className="stat-label">Total Posts</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{blogPosts.filter(p => p.status === 'published').length}</span>
                <span className="stat-label">Published</span>
              </div>
            </div>
            {blogPosts.length > 0 && (
              <div className="detail-section">
                <p><strong>Recent Posts:</strong></p>
                <div className="category-summary">
                  {blogPosts.slice(0, 3).map(post => (
                    <span key={post.id} className="category-tag">
                      {post.title.length > 20 ? post.title.substring(0, 20) + '...' : post.title}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Data Summary Card */}
        <div className="dashboard-card">
          <h2>📊 Data Summary</h2>
          <div className="card-content">
            <div className="data-stats">
              <div className="stat-item">
                <span className="stat-number">{profile?.interests?.length || 0}</span>
                <span className="stat-label">Interests</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{(goals?.shortTerm?.length || 0) + (goals?.longTerm?.length || 0)}</span>
                <span className="stat-label">Total Goals</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{projects?.activeProjects?.length || 0}</span>
                <span className="stat-label">Active Projects</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AddGoalModal
        isOpen={showAddGoalModal}
        onClose={() => setShowAddGoalModal(false)}
        onGoalAdded={handleGoalAdded}
      />
      <AddBookmarkModal
        isOpen={showAddBookmarkModal}
        onClose={() => setShowAddBookmarkModal(false)}
        onBookmarkAdded={handleBookmarkAdded}
      />
      <AddContactModal
        isOpen={showAddContactModal}
        onClose={() => setShowAddContactModal(false)}
        onContactAdded={handleContactAdded}
      />
      <AddProjectModal
        isOpen={showAddProjectModal}
        onClose={() => setShowAddProjectModal(false)}
        onProjectAdded={handleProjectAdded}
      />
      <AddHabitModal
        isOpen={showAddHabitModal}
        onClose={() => setShowAddHabitModal(false)}
        onHabitAdded={handleHabitAdded}
      />
      <LogHabitModal
        isOpen={showLogHabitModal}
        onClose={() => setShowLogHabitModal(false)}
        onHabitLogged={handleHabitLogged}
      />
      <AddVehicleModal
        isOpen={showAddVehicleModal}
        onClose={() => setShowAddVehicleModal(false)}
        onVehicleAdded={handleVehicleAdded}
      />
      <AddBlogModal
        isOpen={showAddBlogModal}
        onClose={() => setShowAddBlogModal(false)}
        onBlogAdded={handleBlogAdded}
      />
    </div>
  );
};

export default Dashboard;